import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";

// Mock wallet detail + transactions
const mockWallet = {
  id: "WLT001",
  user: "Alice Johnson",
  balance: 1250.75,
  status: "Active",
  currency: "CAD",
  createdAt: "2023-01-10",
};

const mockTransactions = Array.from({ length: 42 }).map((_, i) => ({
  id: `TXN${1000 + i}`,
  type: i % 3 === 0 ? "Funding" : i % 3 === 1 ? "Transfer" : "Withdrawal",
  amount: (Math.random() * 1000).toFixed(2),
  status: i % 4 === 0 ? "Pending" : "Success",
  date: `2023-05-${String((i % 28) + 1).padStart(2, "0")}`,
}));

export default function WalletDetailsPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  // State hooks (always at top)
  const [wallet, setWallet] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionMessage, setActionMessage] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const perPage = 10;

  // Effect hook for loading data
  useEffect(() => {
    // Simulate API fetch
    setWallet(mockWallet);
    setTransactions(mockTransactions);
  }, [id]);

  // Memoized filtered transactions
  const filteredTransactions = useMemo(() => {
    const lowerSearch = search.toLowerCase();
    return transactions.filter(
      (tx) =>
        tx.id.toLowerCase().includes(lowerSearch) ||
        tx.type.toLowerCase().includes(lowerSearch) ||
        tx.status.toLowerCase().includes(lowerSearch)
    );
  }, [transactions, search]);

  // Pagination calculations
  const totalPages = Math.ceil(filteredTransactions.length / perPage);
  const paginatedTransactions = useMemo(() => {
    const start = (page - 1) * perPage;
    return filteredTransactions.slice(start, start + perPage);
  }, [filteredTransactions, page, perPage]);

  // Early return if wallet not loaded
  if (!wallet) {
    return <div className="p-6">Loading wallet data...</div>;
  }

  // Handle admin actions with simulated delay
  const handleAction = (action) => {
    setActionLoading(true);
    setActionMessage("");

    setTimeout(() => {
      setActionLoading(false);
      setActionMessage(`Wallet ${action} action completed.`);

      if (action === "freeze") {
        setWallet((prev) => ({ ...prev, status: "Frozen" }));
      } else if (action === "close") {
        setWallet((prev) => ({ ...prev, status: "Closed" }));
      } else if (action === "fund") {
        setWallet((prev) => ({ ...prev, balance: prev.balance + 100 }));
      }
    }, 1000);
  };

  return (
    <div className="p-6 bg-white dark:bg-gray-800 rounded-lg shadow md:ml-64">
      <button
        onClick={() => navigate(-1)}
        className="mb-4 px-3 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
      >
        &larr; Back to Wallets
      </button>

      <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white">
        Wallet Details - {wallet.id}
      </h2>

      {/* Admin Action Buttons */}
      <div className="mb-6 space-x-3">
        <button
          disabled={actionLoading || wallet.status !== "Active"}
          onClick={() => handleAction("fund")}
          className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
        >
          Fund Wallet (+$100)
        </button>
        <button
          disabled={actionLoading || wallet.status !== "Active"}
          onClick={() => handleAction("freeze")}
          className="px-4 py-2 bg-yellow-600 text-white rounded hover:bg-yellow-700 disabled:opacity-50"
        >
          Freeze Wallet
        </button>
        <button
          disabled={actionLoading || wallet.status === "Closed"}
          onClick={() => handleAction("close")}
          className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
        >
          Close Wallet
        </button>
      </div>

      {actionMessage && (
        <div className="mb-6 text-green-700 dark:text-green-400 font-semibold">
          {actionMessage}
        </div>
      )}

      {/* Wallet Info */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-gray-800 dark:text-gray-200 mb-8">
        <div>
          <p>
            <span className="font-semibold">Wallet ID:</span> {wallet.id}
          </p>
          <p>
            <span className="font-semibold">User:</span> {wallet.user}
          </p>
          <p>
            <span className="font-semibold">Balance:</span>{" "}
            {wallet.balance.toLocaleString(undefined, {
              style: "currency",
              currency: wallet.currency,
            })}
          </p>
          <p>
            <span className="font-semibold">Status:</span> {wallet.status}
          </p>
          <p>
            <span className="font-semibold">Created At:</span> {wallet.createdAt}
          </p>
        </div>
      </div>

      {/* Transaction Search */}
      <div className="mb-4">
        <input
          type="text"
          placeholder="Search transactions by ID, type, or status"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          className="w-full md:w-1/3 p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
        />
      </div>

      {/* Transactions Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full border-collapse border border-gray-200 dark:border-gray-700">
          <thead className="bg-gray-100 dark:bg-gray-700">
            <tr>
              <th className="py-2 px-4 text-left text-sm font-semibold text-gray-700 dark:text-gray-300">
                Transaction ID
              </th>
              <th className="py-2 px-4 text-left text-sm font-semibold text-gray-700 dark:text-gray-300">
                Type
              </th>
              <th className="py-2 px-4 text-right text-sm font-semibold text-gray-700 dark:text-gray-300">
                Amount
              </th>
              <th className="py-2 px-4 text-left text-sm font-semibold text-gray-700 dark:text-gray-300">
                Status
              </th>
              <th className="py-2 px-4 text-left text-sm font-semibold text-gray-700 dark:text-gray-300">
                Date
              </th>
            </tr>
          </thead>
          <tbody>
            {paginatedTransactions.length === 0 ? (
              <tr>
                <td
                  colSpan="5"
                  className="text-center py-6 text-gray-500 dark:text-gray-400"
                >
                  No transactions found.
                </td>
              </tr>
            ) : (
              paginatedTransactions.map((tx) => (
                <tr
                  key={tx.id}
                  className="border-t border-gray-200 dark:border-gray-700 hover:bg-blue-50 dark:hover:bg-gray-700"
                >
                  <td className="py-3 px-4">{tx.id}</td>
                  <td className="py-3 px-4">{tx.type}</td>
                  <td className="py-3 px-4 text-right font-mono">
                    {Number(tx.amount).toLocaleString(undefined, {
                      style: "currency",
                      currency: wallet.currency,
                    })}
                  </td>
                  <td className="py-3 px-4">{tx.status}</td>
                  <td className="py-3 px-4">{tx.date}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination Controls */}
      <div className="mt-4 flex justify-between items-center text-gray-700 dark:text-gray-300">
        <button
          onClick={() => setPage((p) => Math.max(p - 1, 1))}
          disabled={page === 1}
          className="px-3 py-1 bg-blue-500 text-white rounded disabled:opacity-50"
        >
          Previous
        </button>
        <span>
          Page {page} of {totalPages}
        </span>
        <button
          onClick={() => setPage((p) => Math.min(p + 1, totalPages))}
          disabled={page === totalPages}
          className="px-3 py-1 bg-blue-500 text-white rounded disabled:opacity-50"
        >
          Next
        </button>
      </div>
    </div>
  );
}
